<script setup>
// One Stage canonical Toolbar primitive.
// A horizontal action surface — page headers, filter bars, list controls.
// Composition: <slot name="leading" /> | <slot /> | <slot name="trailing" />.
//
// Optional sticky positioning with backdrop blur (e.g. for scrolling lists).

import { computed, useSlots } from 'vue';

const props = defineProps({
  /** Visual prominence. Quiet = transparent w/ border, panel = surface w/ shadow. */
  variant: {
    type: String,
    default: 'quiet',
    validator: v => ['quiet', 'panel'].includes(v),
  },
  /** Vertical padding density. */
  density: {
    type: String,
    default: 'comfortable',
    validator: v => ['comfortable', 'compact'].includes(v),
  },
  /** Sticky to the top of its scroll container. */
  sticky: { type: Boolean, default: false },
  /** Translucent + blurred backdrop (use with sticky). */
  glass: { type: Boolean, default: false },
  /** Stack leading/default/trailing vertically below md breakpoint. */
  responsive: { type: Boolean, default: false },
});

const slots = useSlots();

const padding = computed(() => ({
  comfortable: 'px-5 py-3',
  compact: 'px-4 py-2',
}[props.density]));
</script>

<template>
  <div
    :class="[
      'os-tx-colors flex items-center gap-3 border-n-weak',
      padding,
      variant === 'panel'
        ? 'bg-n-solid-1 dark:bg-n-solid-2 border os-r-lg os-elev-1'
        : 'border-b',
      glass && 'bg-n-solid-1/70 dark:bg-n-solid-2/70 backdrop-blur-md backdrop-saturate-150',
      sticky && 'sticky top-0 z-10',
      responsive && 'flex-col items-stretch md:flex-row md:items-center',
    ]"
    role="toolbar"
  >
    <div v-if="slots.leading" class="flex items-center gap-2 min-w-0">
      <slot name="leading" />
    </div>
    <div v-if="slots.default" class="flex-1 flex items-center gap-2 min-w-0">
      <slot />
    </div>
    <div v-if="slots.trailing" class="flex items-center gap-2 flex-shrink-0">
      <slot name="trailing" />
    </div>
  </div>
</template>
